/**
 * One application, and the buttons that move it.
 *
 * The button set comes from `available_actions`, which the server computes from
 * the same transition table the API enforces. The client never decides what is
 * possible -- if it did, the two would drift and captains would tap buttons
 * that 409.
 *
 * Anchor down and anchor up are the largest controls in the app and they take a
 * one-shot GPS fix at the moment of the tap. They are deliberately *not*
 * queueable offline: a fix means "here, now", and replaying one an hour later
 * from a queue would file a position the vessel has left. Offline, they fail
 * and say so.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Notice, Spinner, StateBadge, formatLoa, formatPosition } from '@abg/ui'
import { formatWindowForCaptain, relative } from '@abg/ui/time'
import { AlternativeOffer } from '../components/AlternativeOffer'
import { api, ApiError, OfflineError } from '../lib/api'
import { getFix, GeolocationFailed } from '../lib/geo'
import type { Application } from '../lib/types'

/**
 * The same vessel, booked in two places at once.
 *
 * Specific rather than loud, which is the useful reading of "very visual": it
 * names the other application, its window and its place, so the captain can
 * decide which one is wrong without opening it. A banner saying "you have a
 * conflict" would be a nag.
 *
 * The copy turns on `filed_later`, from the server. ABG's rule is that a new
 * application overrides an old one, so on the newer row the older ones are
 * what to adjust -- and on an older row it is this one that has probably been
 * replaced. Telling a captain to withdraw what they just filed would be
 * exactly backwards.
 *
 * No action button here. Withdrawing is done on the application being
 * withdrawn, where the captain can see what they are closing -- a one-tap
 * withdraw of a *different* application from this screen is how the wrong one
 * gets closed.
 */
function OverlapWarning({ application }: { application: Application }) {
  const superseded = application.overlaps.filter((o) => o.filed_later)
  const earlier = application.overlaps.filter((o) => !o.filed_later)

  return (
    <Notice tone="warn">
      <strong>
        {application.overlaps.length === 1
          ? 'This overlaps another of your applications'
          : `This overlaps ${application.overlaps.length} of your other applications`}
      </strong>
      <p className="small">
        We have kept them all — nothing has been refused or changed. But the
        vessel cannot be in two places at once, so one of them needs adjusting
        or withdrawing before the window arrives.
      </p>

      {earlier.length > 0 && (
        <div className="stack-sm">
          <span className="caption">
            Filed before this one, so this application probably replaces{' '}
            {earlier.length === 1 ? 'it' : 'them'}:
          </span>
          <ul className="usage-list">
            {earlier.map((o) => (
              <li key={o.id}>
                <Link to={`/applications/${o.id}`}>
                  <span>{o.anchorage_name ?? 'Unregistered position'}</span>
                </Link>
                <span className="mono">{formatWindowForCaptain(o.window_start, o.window_end)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {superseded.length > 0 && (
        <div className="stack-sm">
          <span className="caption">
            Filed after this one, so {superseded.length === 1 ? 'it' : 'they'} may
            already replace this application:
          </span>
          <ul className="usage-list">
            {superseded.map((o) => (
              <li key={o.id}>
                <Link to={`/applications/${o.id}`}>
                  <span>{o.anchorage_name ?? 'Unregistered position'}</span>
                </Link>
                <span className="mono">{formatWindowForCaptain(o.window_start, o.window_end)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Notice>
  )
}

export function ApplicationDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [application, setApplication] = useState<Application | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)

  const load = useCallback(async () => {
    try {
      setApplication(await api.get<Application>(`/api/applications/${id}`))
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 404) setNotFound(true)
      else setError(caught instanceof Error ? caught.message : 'Could not load this application.')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  const act = async (label: string, run: () => Promise<Application>) => {
    setBusy(label)
    setError(null)
    try {
      setApplication(await run())
    } catch (caught) {
      if (caught instanceof OfflineError) {
        setError('No connection. This action needs to reach us — try again when you have signal.')
      } else {
        setError(caught instanceof ApiError ? caught.message : 'That did not work.')
      }
    } finally {
      setBusy(null)
    }
  }

  const withFix = async (label: string, path: string) => {
    setBusy(label)
    setError(null)
    try {
      const fix = await getFix()
      setApplication(
        await api.post<Application>(path, {
          lat: fix.lat,
          lon: fix.lon,
          accuracy_m: fix.accuracyM,
          reported_at: fix.at,
        }),
      )
    } catch (caught) {
      if (caught instanceof GeolocationFailed) setError(caught.message)
      else if (caught instanceof OfflineError) {
        setError('No connection. Report it when you have signal — the window stays valid.')
      } else setError(caught instanceof ApiError ? caught.message : 'That did not work.')
    } finally {
      setBusy(null)
    }
  }

  if (loading) return <div className="page"><Spinner label="Loading…" /></div>
  if (notFound) {
    return (
      <div className="page">
        <Notice tone="alert">
          That application is not available. If you have changed vessel, applications from your
          previous vessel stay with that vessel.
        </Notice>
        <button type="button" className="btn btn-quiet" onClick={() => navigate('/applications')}>
          Back to my applications
        </button>
      </div>
    )
  }
  if (!application) return <div className="page"><Notice tone="alert">{error}</Notice></div>

  const can = (action: string) => application.available_actions.includes(action)
  const position =
    application.anchor_down_lat !== null && application.anchor_down_lon !== null
      ? { lat: application.anchor_down_lat, lon: application.anchor_down_lon, reported: true }
      : { lat: application.lat, lon: application.lon, reported: false }

  return (
    <div className="page">
      <header className="page-header">
        <div className="row-between">
          <h1>{application.anchorage_name ?? 'Unregistered position'}</h1>
          <StateBadge state={application.state} />
        </div>
        <span className="subtitle">{application.reference}</span>
      </header>

      {error && <Notice tone="alert">{error}</Notice>}

      {application.overlaps.length > 0 && (
        <OverlapWarning application={application} />
      )}

      {application.state === 'alternative_offered' && (
        <AlternativeOffer
          application={application}
          busy={busy}
          onAccept={() =>
            void act('accept', async () => {
              const created = await api.post<Application>(
                `/api/applications/${application.id}/alternative/accept`,
                {},
              )
              // The response is the *new* application, not this one. Navigate,
              // or the captain would be looking at a page whose state has just
              // become `rejected` while the thing they asked for is elsewhere.
              navigate(`/applications/${created.id}`, { replace: true })
              return created
            })
          }
          onDecline={(reason) =>
            void act('decline', () =>
              api.post<Application>(
                `/api/applications/${application.id}/alternative/decline`,
                reason ? { reason } : {},
              ),
            )
          }
        />
      )}

      {application.state === 'rejected' && application.superseded_by_id && (
        <Notice>
          <strong>Replaced.</strong> You accepted the position that was suggested instead, and it
          is now {application.superseded_by_reference}.{' '}
          <button
            type="button"
            className="btn-link"
            onClick={() => navigate(`/applications/${application.superseded_by_id}`)}
          >
            Open it
          </button>
        </Notice>
      )}

      {application.state === 'rejected' &&
        !application.superseded_by_id &&
        application.decision_reason && (
          <Notice tone="alert">
            <strong>Not approved.</strong> {application.decision_reason}
          </Notice>
        )}

      {application.csc_proposed && application.state === 'submitted' && (
        <Notice tone="ok">
          <strong>This is the position that was suggested to you.</strong> It was already agreed
          with the authority, so we only have to confirm it.
          {application.supersedes_reference && (
            <>
              {' '}Replaces {application.supersedes_reference}.
            </>
          )}
        </Notice>
      )}
      {application.state === 'approved' && (
        <Notice tone="ok">
          <strong>Approved.</strong> Report anchor down when you are settled — it is what tells
          us and the harbour authority the berth is in use.
        </Notice>
      )}
      {application.state === 'expired' && (
        <Notice tone="warn">
          The window passed without an anchor-down report, so this has expired. File a new
          application if you still need the spot.
        </Notice>
      )}
      {application.filed_by_agent && (
        <Notice>We filed this application on your behalf.</Notice>
      )}
      {application.anchorage_status === 'discouraged' && (
        <Notice tone="warn">We list this anchorage as discouraged. Check the notes.</Notice>
      )}

      <section className="card">
        <dl className="kv">
          <dt>Vessel</dt>
          <dd>
            {application.vessel_name} · {formatLoa(application.loa_m)}
          </dd>

          <dt>Window</dt>
          <dd>{formatWindowForCaptain(application.window_start, application.window_end)}</dd>

          <dt>Position</dt>
          <dd>
            <span className="mono">{formatPosition(position.lat, position.lon)}</span>
            {position.reported && <div className="caption">Reported at anchor down</div>}
          </dd>

          {application.municipality && (
            <>
              <dt>Municipality</dt>
              <dd>{application.municipality}</dd>
            </>
          )}

          <dt>Visibility</dt>
          <dd>
            {application.visibility === 'visible'
              ? 'Shared with vessels anchoring nearby in the same window'
              : 'Hidden from other vessels'}
          </dd>

          {application.captain_notes && (
            <>
              <dt>Your notes</dt>
              <dd>{application.captain_notes}</dd>
            </>
          )}

          {application.anchor_down_at && (
            <>
              <dt>Anchor down</dt>
              <dd>{relative(application.anchor_down_at)}</dd>
            </>
          )}
          {application.anchor_up_at && (
            <>
              <dt>Anchor up</dt>
              <dd>{relative(application.anchor_up_at)}</dd>
            </>
          )}
        </dl>
      </section>

      <div className="action-stack">
        {can('active') && (
          <button
            type="button"
            className="btn btn-lg btn-block"
            disabled={busy !== null}
            onClick={() => withFix('down', `/api/applications/${application.id}/anchor-down`)}
          >
            {busy === 'down' ? 'Getting a fix…' : '⚓ Anchor down'}
          </button>
        )}

        {can('completed') && (
          <button
            type="button"
            className="btn btn-lg btn-block"
            disabled={busy !== null}
            onClick={() => withFix('up', `/api/applications/${application.id}/anchor-up`)}
          >
            {busy === 'up' ? 'Getting a fix…' : '⚓ Anchor up — leaving'}
          </button>
        )}

        {can('submitted') && (
          <button
            type="button"
            className="btn btn-block"
            disabled={busy !== null}
            onClick={() =>
              act('submit', () =>
                api.post<Application>(`/api/applications/${application.id}/submit`),
              )
            }
          >
            {busy === 'submit' ? 'Sending…' : 'Submit'}
          </button>
        )}

        {/* Suppressed while an offer is open. `available_actions` legitimately
          * lists `withdrawn` there, but AlternativeOffer already owns that
          * choice and asks for a reason with it -- two buttons doing the same
          * thing, one of which quietly discards the reason, is worse than one. */}
        {can('withdrawn') && application.state !== 'alternative_offered' && (
          <button
            type="button"
            className="btn btn-quiet btn-block"
            disabled={busy !== null}
            onClick={() =>
              act('withdraw', () =>
                api.post<Application>(`/api/applications/${application.id}/withdraw`, {
                  reason: null,
                }),
              )
            }
          >
            Withdraw
          </button>
        )}

        {can('cancelled') && (
          <button
            type="button"
            className="btn btn-quiet btn-block"
            disabled={busy !== null}
            onClick={() =>
              act('cancel', () =>
                api.post<Application>(`/api/applications/${application.id}/cancel`, {
                  reason: null,
                }),
              )
            }
          >
            Cancel — not coming
          </button>
        )}

        {application.state === 'draft' && (
          <button
            type="button"
            className="btn btn-quiet btn-block"
            disabled={busy !== null}
            onClick={async () => {
              await api.delete(`/api/applications/${application.id}`)
              navigate('/applications', { replace: true })
            }}
          >
            Delete draft
          </button>
        )}
      </div>
    </div>
  )
}
